import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchSearchHistory,
  getLiteratureReviewHistory,
} from "../../Utils/functions";
import useAccessCheck from "../../hooks/useAccessCheck";
import "./Reports.css";

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const ChevronIcon = () => (
  <svg
    className="reports-row-chevron"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M9.29 6.71a1 1 0 0 0 0 1.41L13.17 12l-3.88 3.88a1 1 0 1 0 1.42 1.41l4.59-4.59a1 1 0 0 0 0-1.41L10.71 6.7a1 1 0 0 0-1.42.01Z"
      fill="currentColor"
    />
  </svg>
);

function Reports() {
  const navigate = useNavigate();
  const location = useLocation();
  const showLiteratureReview =
    new URLSearchParams(location.search).get("type") === "literature-review" ||
    location.hash === "#literature-review";
  const { user, isAdvanced, isEnterpriseActive, loading: accessLoading } =
    useAccessCheck();
  const hasLitReviewAccess = Boolean(user && (isEnterpriseActive || isAdvanced));
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setReports([]);
      setLoading(false);
      return undefined;
    }
    if (accessLoading) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (showLiteratureReview) {
          const lit = hasLitReviewAccess
            ? await getLiteratureReviewHistory()
            : { jobs: [] };
          if (cancelled) return;
          setReports(lit?.jobs || []);
        } else {
          const searchHistory = await fetchSearchHistory();
          if (cancelled) return;
          setReports(Array.isArray(searchHistory) ? searchHistory : []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load reports");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user, accessLoading, hasLitReviewAccess, showLiteratureReview]);

  const openReport = (item) => {
    if (showLiteratureReview) {
      navigate(`/screening/${item.id}#research-report`, {
        state: {
          litreviewtitle: item.litreviewtitle || item.question,
          step: "research-report",
        },
      });
      return;
    }
    navigate(`/search/${item.id}`);
  };

  const heading = showLiteratureReview ? "Literature Review" : "Reports";
  const emptyHint = showLiteratureReview
    ? "Generated literature reviews will appear here."
    : "Completed searches will appear here.";
  const isBusy = loading || accessLoading;

  return (
    <div className="reports-page">
      <div className="reports-panel">
        <aside className="reports-heading-col">
          <h1 className="reports-heading">{heading}</h1>
          <div className="reports-heading-rule" />
        </aside>

        <section className="reports-list-col">
          {isBusy && (
            <div className="reports-status" role="status">
              <span className="reports-spinner" />
              Loading reports
            </div>
          )}

          {!isBusy && error && (
            <div className="reports-status reports-status-error" role="alert">
              {error}
            </div>
          )}

          {!isBusy && !error && reports.length === 0 && (
            <div className="reports-empty">
              <p className="reports-empty-title">No reports yet</p>
              <p className="reports-empty-hint">{emptyHint}</p>
            </div>
          )}

          {!isBusy && !error && reports.length > 0 && (
            <>
              <p className="reports-count">
                {reports.length} {reports.length === 1 ? "report" : "reports"}
              </p>
              <ul className="reports-list">
                {reports.map((item) => {
                  const title = showLiteratureReview
                    ? item.litreviewtitle || item.question
                    : item.question;
                  const dateLabel = formatDate(item.createdAt);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="reports-row"
                        onClick={() => openReport(item)}
                      >
                        <span className="reports-row-text">
                          <span className="reports-row-title">{title}</span>
                          {dateLabel && (
                            <span className="reports-row-meta">{dateLabel}</span>
                          )}
                        </span>
                        <ChevronIcon />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default Reports;
